/**
 * The public `register` mutation's ARGUMENT LIST IS PER TENANT (roadmap O44).
 *
 * The backend builds it from the portal's `portal_fields` rows (admin: Settings →
 * Register form fields): every row is an argument, and a row with the `required`
 * rule is a non-null one. Seven rows are mandatory on every portal — the built-ins
 * below — but an admin can add more, and N24 did (`salutation: String!`). A fixed
 * eight-argument document is then rejected by GraphQL validation before anything
 * runs, and nobody can register.
 *
 * So the form reads the portal's actual argument list (introspected on the server
 * by `register-fields.server.ts`), renders the extras, and BUILDS the document.
 * Pure module: no `server-only`, so the client form and vitest can import it.
 * Labels are the humanised argument name — the public API does not expose the
 * admin's placeholder text (the BE leg of O44); when it does, thread it through
 * `RegisterExtraField` and drop `humanizeFieldName`.
 */

export const REGISTER_BUILTIN_ARGS = [
  "newsletter",
  "name",
  "surname",
  "email",
  "password",
  "repeat_password",
  "terms",
  "gdpr",
] as const;

/** GraphQL scalar the argument takes — the admin's field types map onto two. */
export type RegisterExtraKind = "String" | "Boolean";

export interface RegisterExtraField {
  /** Argument name, doubles as the form input's `name`. */
  name: string;
  kind: RegisterExtraKind;
  /** Non-null argument (`required` rule on the admin row). */
  required: boolean;
}

interface IntrospectedTypeRef {
  kind: string;
  name: string | null;
  ofType?: IntrospectedTypeRef | null;
}

/**
 * Structural shape of the `RegisterArgs` introspection result — the generated
 * `RegisterArgsQuery` satisfies it (its `kind` enum is a string enum), and so do
 * plain-literal test fixtures, which the enum-typed generated type would reject.
 */
export interface IntrospectedRegisterArgs {
  __type: {
    fields: Array<{
      name: string;
      args: Array<{ name: string; type: IntrospectedTypeRef }>;
    }> | null;
  } | null;
}

/** Every `register` argument that is not one of the built-ins, in schema order. */
export function parseRegisterExtraFields(data: IntrospectedRegisterArgs): RegisterExtraField[] {
  const reg = data.__type?.fields?.find((f) => f.name === "register");
  if (!reg) return [];
  const builtin = new Set<string>(REGISTER_BUILTIN_ARGS);
  return reg.args
    .filter((a) => !builtin.has(a.name))
    .map((a) => {
      const required = a.type.kind === "NON_NULL";
      const named = required ? a.type.ofType : a.type;
      return { name: a.name, kind: named?.name === "Boolean" ? "Boolean" : "String", required };
    });
}

// GraphQL name grammar — an argument name that fails it can never reach the document.
const GRAPHQL_NAME = /^[A-Za-z_][A-Za-z0-9_]*$/;

/**
 * The `Register` document for THIS portal: the built-in eight plus the extras.
 * `buildRegisterMutation([])` is `M_REGISTER` (operations.ts, the codegen base) —
 * a unit test pins the two together.
 */
export function buildRegisterMutation(extras: RegisterExtraField[]): string {
  const safe = extras.filter((e) => GRAPHQL_NAME.test(e.name));
  const vars = safe.map((e) => `, $${e.name}: ${e.kind}${e.required ? "!" : ""}`).join("");
  const args = safe.map((e) => `, ${e.name}: $${e.name}`).join("");
  return (
    `mutation Register($email: String!, $password: String!, $repeat_password: String!, ` +
    `$name: String!, $surname: String!, $newsletter: Boolean!, $terms: Boolean!, $gdpr: Boolean!${vars}) { ` +
    `register(email: $email, password: $password, repeat_password: $repeat_password, ` +
    `name: $name, surname: $surname, newsletter: $newsletter, terms: $terms, gdpr: $gdpr${args}) }`
  );
}

/** `salutation` → "Salutation", `phone_country` → "Phone country", `vatId` → "Vat id". */
export function humanizeFieldName(name: string): string {
  const words = name
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}
